import { Link, useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import {
  CheckIcon,
  ChevronRightIcon,
  LayersIcon,
  PlusIcon,
} from "lucide-react";
import { useState } from "react";
import { Focusable } from "react-aria-components";

import { trpc } from "@blis/trpc/react";

import { ActiveHoldIndicator, StatIndicator } from "@/components/Indicators";
import { Badge } from "@/components/ui/badge";
import { ButtonGroup, buttonVariants } from "@/components/ui/button";
import { CommandBox } from "@/components/ui/command-box";
import { StatusIcon } from "@/components/ui/icons";
import { Text } from "@/components/ui/text";
import { useTinyKeys } from "@/lib/useTinyKeys";
import { useAccession } from "./AccessionProvider";
import { TestPreviewHoverCard } from "./TestOrderPreview";

export function AccessionHeader({ actions }: { actions?: React.ReactNode }) {
  const { accession } = useAccession();

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <ButtonGroup className="min-w-0">
          <Link
            to="/subjects/$id"
            params={{ id: accession.subjectId }}
            state={{
              href: { to: "/accessions/$id", params: { id: accession.id } },
            }}
            className={buttonVariants({ variant: "outline" })}
          >
            <div className="flex items-baseline gap-1 overflow-hidden">
              <span className="text-muted-foreground/80 text-[0.6rem] uppercase hidden md:block">
                Donor
              </span>
              <span className="truncate">{accession.caseId}</span>
            </div>
          </Link>
          <RelatedDropdown />
        </ButtonGroup>
      </div>
      {actions}
    </div>
  );
}

function RelatedDropdown() {
  const [open, setOpen] = useState(false);

  useTinyKeys({
    "Shift+r": (e) => {
      e.preventDefault();
      setOpen((prev) => !prev);
    },
  });
  const { accession } = useAccession();
  const { data, isPending } = trpc.accession.related.useQuery({
    subjectId: accession.subjectId,
  });
  const navigate = useNavigate();

  return (
    <CommandBox
      aria-label="Related accessions"
      isOpen={open}
      onOpenChange={setOpen}
    >
      <CommandBox.Button
        variant="outline"
        isDisabled={isPending}
        className="flex items-center text-muted-foreground hover:text-primary gap-1.5 font-normal"
        onPress={() => setOpen(true)}
        hideIndicator
      >
        <span className="hidden sm:inline text-xs mr-0.5">
          {data?.length || 0}
        </span>
        <LayersIcon className="size-3" />
        <ChevronRightIcon className="size-3 shrink-0" />
      </CommandBox.Button>
      <CommandBox.Popover
        className="p-0 w-full md:w-[400px]"
        placement="right top"
      >
        <CommandBox.Input placeholder="Search accessions..." />
        <CommandBox.Empty>No related accessions</CommandBox.Empty>
        <CommandBox.List>
          <CommandBox.Section heading={`Accessions for ${accession.caseId}`}>
            {(data || []).map((acc) => (
              <TestPreviewHoverCard accession={acc} key={acc.id}>
                <Focusable>
                  <CommandBox.Item
                    key={acc.id}
                    id={acc.id}
                    isDisabled={acc.id === accession.id}
                    value={`${acc.id.toString()} ${acc.testType}`}
                    onSelect={() => {
                      setOpen(false);
                      navigate({
                        to: "/accessions/$id",
                        params: { id: acc.id },
                        state: {
                          href: {
                            to: "/accessions/$id",
                            params: { id: acc.id },
                          },
                        },
                      });
                    }}
                    className="flex items-center justify-between gap-2"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-3 flex justify-center items-center">
                        {acc.id === accession.id && (
                          <CheckIcon className="size-3" />
                        )}
                      </div>
                      <StatusIcon status={acc.status} />
                      <Text size="2" mono>
                        {acc.id}
                      </Text>
                      <Text size="1" mono muted>
                        {dayjs(acc.date).fromNow()}
                      </Text>
                      {acc._count.holds > 0 && <ActiveHoldIndicator />}
                      {acc.priority === "STAT" && <StatIndicator />}
                    </div>
                    <Badge size="sm">{acc.testType}</Badge>
                  </CommandBox.Item>
                </Focusable>
              </TestPreviewHoverCard>
            ))}
          </CommandBox.Section>
          <CommandBox.Separator />
          <CommandBox.Section>
            <CreateAccessionItem />
          </CommandBox.Section>
        </CommandBox.List>
      </CommandBox.Popover>
    </CommandBox>
  );
}

function CreateAccessionItem() {
  const { accession } = useAccession();
  const navigate = useNavigate();
  return (
    <CommandBox.Item
      value="create new accession"
      id="create"
      onSelect={() => {
        navigate({
          to: "/subjects/$id/create",
          params: { id: accession.subjectId },
          state: {
            href: { to: "/accessions" },
          },
        });
      }}
    >
      <PlusIcon className="mr-2 size-4" />
      Create new accession
    </CommandBox.Item>
  );
}
